import { useCallback, useEffect, useRef, useState } from 'react';
import { Client, type Room } from 'colyseus.js';
import {
  ANSWER_WINDOW_MS,
  MAX_MP_PLAYERS,
  readingMsForPrompt,
  TIMED_DURATIONS_SEC,
  type QuizScoreboardEntry,
} from '@sb2/shared';
import { type QuizQuestion, wsUrl } from '../../api/client';
import { useLocale } from '../../i18n/LocaleContext';
import { useAuth } from '../AuthContext';
import { QuizBoard } from '../components/QuizBoard';

type UiPhase = 'menu' | 'lobby' | 'countdown' | 'question' | 'reveal' | 'ended';

type ListedRoom = {
  roomId: string;
  clients: number;
  maxClients: number;
  metadata?: {
    code?: string;
    private?: boolean;
    quick?: boolean;
    durationSec?: number;
    maxPlayers?: number;
    playerCount?: number;
    phase?: string;
    open?: boolean;
  };
};

const CODE_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';

function makeRoomCode(len = 6): string {
  let out = '';
  for (let i = 0; i < len; i++) {
    out += CODE_ALPHABET[Math.floor(Math.random() * CODE_ALPHABET.length)]!;
  }
  return out;
}

export function LobbyPage() {
  const { user, ensureGuest } = useAuth();
  const { t, locale } = useLocale();
  const [phase, setPhase] = useState<UiPhase>('menu');
  const [durationSec, setDurationSec] = useState<number>(TIMED_DURATIONS_SEC[0]);
  const [maxPlayers, setMaxPlayers] = useState(2);
  const [code, setCode] = useState('');
  const [roomCode, setRoomCode] = useState<string | null>(null);
  const [isPrivate, setIsPrivate] = useState(false);
  const [players, setPlayers] = useState<QuizScoreboardEntry[]>([]);
  const [question, setQuestion] = useState<QuizQuestion | null>(null);
  const [deadlineAt, setDeadlineAt] = useState<number | null>(null);
  const [countdown, setCountdown] = useState(0);
  const [locked, setLocked] = useState(false);
  const [showChoices, setShowChoices] = useState(false);
  const [winners, setWinners] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [remainingMs, setRemainingMs] = useState(0);
  const [publicRooms, setPublicRooms] = useState<ListedRoom[]>([]);
  const [listing, setListing] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [rematchVotes, setRematchVotes] = useState(0);
  const [rematchNeeded, setRematchNeeded] = useState(0);
  const [codeCopied, setCodeCopied] = useState(false);
  const roomRef = useRef<Room | null>(null);
  const readingTimer = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      roomRef.current?.leave();
      roomRef.current = null;
      if (readingTimer.current != null) window.clearTimeout(readingTimer.current);
    };
  }, []);

  const refreshPublicRooms = useCallback(async () => {
    try {
      setListing(true);
      const client = new Client(wsUrl());
      const rooms = (await client.getAvailableRooms('quiz')) as ListedRoom[];
      setPublicRooms(
        rooms.filter((r) => {
          const m = r.metadata;
          if (!m || m.private || m.phase !== 'lobby') return false;
          if (m.open === false) return false;
          const count = m.playerCount ?? r.clients;
          const max = m.maxPlayers ?? r.maxClients;
          return count < max;
        }),
      );
    } catch {
      // listing is best-effort
    } finally {
      setListing(false);
    }
  }, []);

  useEffect(() => {
    if (phase !== 'menu') return;
    void refreshPublicRooms();
    const id = window.setInterval(() => void refreshPublicRooms(), 3000);
    return () => window.clearInterval(id);
  }, [phase, refreshPublicRooms]);

  const bindRoom = (room: Room) => {
    roomRef.current = room;
    setPhase('lobby');

    room.onMessage(
      'lobby',
      (msg: {
        phase: string;
        code?: string;
        private?: boolean;
        players: QuizScoreboardEntry[];
      }) => {
        setPlayers(Array.isArray(msg.players) ? msg.players : []);
        if (msg.code) setRoomCode(String(msg.code));
        if (typeof msg.private === 'boolean') setIsPrivate(msg.private);
        if (msg.phase === 'lobby') setPhase('lobby');
      },
    );

    room.onMessage('countdown', (msg: { seconds: number }) => {
      setPhase('countdown');
      setCountdown(msg.seconds);
    });

    room.onMessage(
      'question',
      (msg: {
        question: QuizQuestion;
        deadlineAt: number;
        remainingMs: number;
        readingMs?: number;
      }) => {
        if (readingTimer.current != null) window.clearTimeout(readingTimer.current);
        const readMs = msg.readingMs ?? readingMsForPrompt(msg.question.prompt);
        setPhase('question');
        setQuestion(msg.question);
        setDeadlineAt(null);
        setRemainingMs(msg.remainingMs);
        setShowChoices(false);
        setLocked(true);
        readingTimer.current = window.setTimeout(() => {
          readingTimer.current = null;
          // 5s answer clock from unlock; server absolute deadline still applies.
          setDeadlineAt(Math.min(msg.deadlineAt, Date.now() + ANSWER_WINDOW_MS));
          setShowChoices(true);
          setLocked(false);
        }, readMs);
      },
    );

    room.onMessage('reveal', (msg: { scores: QuizScoreboardEntry[] }) => {
      if (readingTimer.current != null) {
        window.clearTimeout(readingTimer.current);
        readingTimer.current = null;
      }
      setPlayers(msg.scores || []);
      setLocked(true);
      setShowChoices(true);
      setPhase('reveal');
    });

    room.onMessage('ended', (msg: { scores: QuizScoreboardEntry[]; winnerIds: string[] }) => {
      setPhase('ended');
      setPlayers(msg.scores || []);
      setWinners(msg.winnerIds || []);
      setRematchVotes(0);
      setRematchNeeded(0);
    });

    room.onMessage('rematchVotes', (msg: { votes: number; needed: number }) => {
      setRematchVotes(msg.votes);
      setRematchNeeded(msg.needed);
    });

    room.onMessage('rematchReady', () => {
      setPhase('lobby');
      setRematchVotes(0);
      setRematchNeeded(0);
      setWinners([]);
      setQuestion(null);
    });

    room.onMessage('error', (msg: { message?: string }) => {
      setError(msg?.message || t('lobbyConnectFail'));
    });

    room.onError((code, message) => {
      const parts = [code, message].filter(
        (v) => v != null && String(v) !== 'undefined' && String(v).length > 0,
      );
      setError(parts.length ? parts.join(': ') : t('lobbyConnectFail'));
    });
  };

  const formatConnectError = (e: unknown) => {
    if (e instanceof Error && e.message.trim()) return e.message;
    if (e && typeof e === 'object' && 'message' in e) {
      const msg = String((e as { message?: unknown }).message || '').trim();
      if (msg && msg !== 'undefined') return msg;
    }
    return t('lobbyConnectFail');
  };

  const connect = async (opts: Record<string, unknown>) => {
    setError(null);
    setConnecting(true);
    try {
      const profile = await ensureGuest();
      const client = new Client(wsUrl());
      const room = await client.joinOrCreate('quiz', {
        ...opts,
        userId: profile.id,
        username: profile.username || undefined,
        locale,
      });
      bindRoom(room);
    } catch (e) {
      setError(formatConnectError(e));
    } finally {
      setConnecting(false);
    }
  };

  const joinListed = async (roomId: string) => {
    setError(null);
    setConnecting(true);
    try {
      const profile = await ensureGuest();
      const client = new Client(wsUrl());
      const room = await client.joinById(roomId, {
        userId: profile.id,
        username: profile.username || undefined,
        locale,
      });
      bindRoom(room);
    } catch (e) {
      setError(formatConnectError(e));
      void refreshPublicRooms();
    } finally {
      setConnecting(false);
    }
  };

  const quickMatch = () =>
    void connect({
      quick: true,
      private: false,
      matchKey: `quick:${durationSec}:${maxPlayers}`,
      durationSec,
      maxPlayers,
      ranked: maxPlayers === 2,
    });

  const createPublic = () => {
    const roomCodeNext = makeRoomCode();
    void connect({
      private: false,
      quick: false,
      code: roomCodeNext,
      matchKey: `pub:${roomCodeNext}`,
      durationSec,
      maxPlayers,
      ranked: false,
    });
  };

  const createPrivate = () => {
    const roomCodeNext = (code.trim() || makeRoomCode()).toUpperCase();
    setCode(roomCodeNext);
    void connect({
      private: true,
      quick: false,
      code: roomCodeNext,
      matchKey: `priv:${roomCodeNext}`,
      durationSec,
      maxPlayers,
      ranked: false,
    });
  };

  const joinPrivate = () => {
    const roomCodeNext = code.trim().toUpperCase();
    if (!roomCodeNext) {
      setError(t('lobbyEnterCode'));
      return;
    }
    void connect({
      private: true,
      quick: false,
      code: roomCodeNext,
      matchKey: `priv:${roomCodeNext}`,
      durationSec,
      maxPlayers,
      ranked: false,
    });
  };

  const ready = () => roomRef.current?.send('ready', { ready: true });

  const answer = (choiceIndex: number | null) => {
    if (!question || locked || !showChoices || !roomRef.current) return;
    setLocked(true);
    roomRef.current.send('answer', { questionId: question.id, choiceIndex });
  };

  useEffect(() => {
    if (phase !== 'question' || !deadlineAt || locked || !showChoices) return;
    const ms = deadlineAt - Date.now();
    if (ms <= 0) {
      answer(null);
      return;
    }
    const timer = window.setTimeout(() => answer(null), ms);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, deadlineAt, question?.id, locked, showChoices]);

  if (phase === 'menu') {
    return (
      <section className="panel">
        <h1>{t('lobbyTitle')}</h1>
        <p className="muted">{t('lobbyIntro', { max: MAX_MP_PLAYERS })}</p>
        {error ? <p className="error">{error}</p> : null}
        {connecting ? <p className="muted">{t('lobbyConnecting')}</p> : null}

        <div className="duration-row">
          {TIMED_DURATIONS_SEC.map((d) => (
            <button
              key={d}
              type="button"
              className={durationSec === d ? 'btn primary' : 'btn'}
              onClick={() => setDurationSec(d)}
              disabled={connecting}
            >
              {d}
              {t('soloSec')}
            </button>
          ))}
        </div>
        <label className="field">
          {t('lobbyPlayers')}
          <select
            value={maxPlayers}
            onChange={(e) => setMaxPlayers(Number(e.target.value))}
            disabled={connecting}
          >
            {[2, 3, 4, 5].map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </label>

        <div className="cta-row">
          <button type="button" className="btn primary" onClick={quickMatch} disabled={connecting}>
            {connecting ? t('lobbyConnecting') : t('lobbyQuick')}
          </button>
          <button type="button" className="btn" onClick={createPublic} disabled={connecting}>
            {t('lobbyCreatePublic')}
          </button>
        </div>

        <div className="public-rooms">
          <div className="public-rooms-head">
            <strong>{t('lobbyPublicRooms')}</strong>
            <button type="button" className="btn" onClick={() => void refreshPublicRooms()}>
              {listing ? t('loading') : t('lobbyRefresh')}
            </button>
          </div>
          {publicRooms.length === 0 ? (
            <p className="muted">{t('lobbyNoPublic')}</p>
          ) : (
            <ul className="room-list">
              {publicRooms.map((r) => {
                const m = r.metadata || {};
                const count = m.playerCount ?? r.clients;
                const max = m.maxPlayers ?? r.maxClients;
                const secs = m.durationSec ?? TIMED_DURATIONS_SEC[0];
                return (
                  <li key={r.roomId} className="room-row">
                    <span>
                      {m.code || r.roomId.slice(0, 6)} · {count}/{max} · {secs}
                      {t('soloSec')}
                    </span>
                    <button
                      type="button"
                      className="btn primary"
                      disabled={connecting}
                      onClick={() => void joinListed(r.roomId)}
                    >
                      {t('lobbyJoin')}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="private-block">
          <strong>{t('lobbyPrivate')}</strong>
          <p className="muted">{t('lobbyPrivateHint')}</p>
          <div className="field-row">
            <input
              placeholder={t('lobbyRoomCode')}
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
            />
            <button type="button" className="btn" onClick={createPrivate} disabled={connecting}>
              {t('lobbyCreate')}
            </button>
            <button type="button" className="btn" onClick={joinPrivate} disabled={connecting}>
              {t('lobbyJoin')}
            </button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="panel play-panel">
      {error ? <p className="error">{error}</p> : null}
      <div className="hud">
        <div className="hud-stats">
          {phase === 'question' || phase === 'reveal' ? (
            <span>{t('lobbyMatchLeft', { sec: Math.ceil(remainingMs / 1000) })}</span>
          ) : (
            <span className="muted">
              {roomCode
                ? `${isPrivate ? t('lobbyPrivate') : t('lobbyPublic')}: ${roomCode}`
                : t('lobbyTitle')}
            </span>
          )}
        </div>
      </div>

      {phase === 'lobby' && roomCode ? (
        <p className="room-code-banner">
          {t('lobbyShareCode')}: <strong>{roomCode}</strong>{' '}
          <button
            type="button"
            className="btn"
            onClick={() => {
              void navigator.clipboard?.writeText(roomCode).then(() => {
                setCodeCopied(true);
                window.setTimeout(() => setCodeCopied(false), 1500);
              });
            }}
          >
            {codeCopied ? t('lobbyCopiedCode') : t('lobbyShareCode')}
          </button>
        </p>
      ) : null}

      <div className="scoreboard">
        {players.map((p, i) => (
          <div key={p.userId || String(i)} className={`score-row ${p.userId === user?.id ? 'me' : ''}`}>
            <span>
              #{i + 1} {p.username?.trim() ? p.username : t('guest')}
              {!p.connected ? ` (${t('lobbyAway')})` : ''}
              {p.ready && phase === 'lobby' ? ' ✓' : ''}
            </span>
            <span>{p.score ?? 0}</span>
          </div>
        ))}
      </div>

      {phase === 'lobby' ? (
        <div className="lobby-actions">
          <p className="muted">{t('lobbyWaiting')}</p>
          <button type="button" className="btn primary" onClick={ready}>
            {t('lobbyReady')}
          </button>
        </div>
      ) : null}

      {phase === 'countdown' ? <p className="countdown-big">{countdown}</p> : null}

      {phase === 'question' && question ? (
        <QuizBoard
          key={question.id}
          question={question}
          deadlineAt={deadlineAt}
          windowMs={ANSWER_WINDOW_MS}
          showChoices={showChoices}
          disabled={locked || !showChoices}
          selected={null}
          highlightCorrect={null}
          onAnswer={(i) => answer(i)}
        />
      ) : null}
      {phase === 'reveal' ? <p className="muted">{t('lobbyNext')}</p> : null}

      {phase === 'ended' ? (
        <div className="end-panel">
          <h2>{t('lobbyFinished')}</h2>
          <p>
            {winners.length > 1 ? t('lobbyWinners') : t('lobbyWinner')}:{' '}
            {players
              .filter((p) => winners.includes(p.userId))
              .map((p) => (p.username?.trim() ? p.username : t('guest')))
              .join(', ') || '—'}
          </p>
          <button type="button" className="btn" onClick={() => roomRef.current?.send('rematch')}>
            {rematchNeeded > 0
              ? t('lobbyRematchVotes', { votes: rematchVotes, needed: rematchNeeded })
              : t('lobbyRematch')}
          </button>
          <button
            type="button"
            className="btn primary"
            onClick={() => {
              roomRef.current?.leave();
              roomRef.current = null;
              setPhase('menu');
              setRoomCode(null);
              setPlayers([]);
            }}
          >
            {t('lobbyLeave')}
          </button>
        </div>
      ) : null}
    </section>
  );
}
